import { useEffect, useState } from 'react';

import { switchedFigure, type FigureMode } from '@core/bookings/moneyChoice';
import { formatAmount } from '@core/format/money';

/**
 * One of the bag's money figures — discount, token, deposit — as the canvas draws it (2026-10-26):
 * a % / ₹ switch, quick buttons for the usual figures, and a box to type any figure. Switching keeps
 * the money (5% of ₹10,000 becomes ₹500). The figure is the caller's; this only says what was chosen.
 */
export function PercentOrAmount({
  title,
  aside,
  mode,
  figure,
  money,
  base,
  pctChoices,
  amountChoices,
  zeroLabel,
  pctLabel = (pct) => `${pct}%`,
  currency,
  disabled,
  tinted = false,
  onChange,
  children,
}: {
  title: string;
  /** Beside the title, small — "at pickup, back on return". */
  aside?: string | undefined;
  mode: FigureMode;
  /** The figure in its own mode — 5 for 5%, 500 for ₹500; null is an empty box. */
  figure: number | null;
  /** What the figure comes to in rupees now — what a switch of mode keeps. */
  money: number;
  /** What a percentage is of. */
  base: number;
  pctChoices: readonly number[];
  amountChoices: readonly number[];
  /** The quick button for nothing — "No discount", "No token". */
  zeroLabel?: string | undefined;
  pctLabel?: (pct: number) => string;
  currency: string;
  disabled: boolean;
  /** The token's light wash, so the money taken now stands out. */
  tinted?: boolean;
  onChange: (mode: FigureMode, value: number | null) => void;
  children?: React.ReactNode;
}) {
  const shown = figure === null ? '' : String(figure);
  const [text, setText] = useState(shown);

  // A quick button or a switch changes the figure from outside; the box follows unless it already
  // says the same number ("7." while typing 7.5 is still 7).
  useEffect(() => {
    setText((was) => (Number(was) === figure && was !== '' ? was : shown));
  }, [figure, shown]);

  const choices = mode === 'pct' ? pctChoices : amountChoices;
  const typedOwn = text !== '' && !choices.some((choice) => choice === Number(text));
  const label = (choice: number) =>
    choice === 0 && zeroLabel
      ? zeroLabel
      : mode === 'pct'
        ? pctLabel(choice)
        : `${currency}${formatAmount(choice)}`;

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-xl p-3 ${tinted ? 'bg-primary-transparent' : 'border-border-color border'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="mb-0 font-semibold text-gray-900">
          {title}
          {aside && <span className="text-default ms-1 text-[12px] font-normal">· {aside}</span>}
        </p>
        <div
          role="group"
          aria-label={`${title} as`}
          className="border-border-color inline-flex rounded-lg border bg-white p-0.5"
        >
          {(['pct', 'amount'] as const).map((each) => (
            <button
              key={each}
              type="button"
              disabled={disabled}
              aria-pressed={mode === each}
              onClick={() => {
                if (each !== mode) {
                  onChange(each, switchedFigure(each, money, base));
                }
              }}
              className={`h-[30px] min-w-10 cursor-pointer rounded-md px-3 text-[13px] font-bold ${
                mode === each ? 'bg-primary text-white' : 'text-gray-900 hover:bg-light'
              }`}
            >
              {each === 'pct' ? '%' : currency || '₹'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {choices.map((choice) => (
          <button
            key={choice}
            type="button"
            disabled={disabled}
            aria-pressed={!typedOwn && Number(text || 0) === choice}
            onClick={() => onChange(mode, choice)}
            className={`h-[34px] cursor-pointer rounded-full border px-3 text-[13px] ${
              !typedOwn && Number(text || 0) === choice
                ? 'border-primary bg-primary font-medium text-white'
                : 'border-border-color bg-white text-gray-900 hover:bg-light'
            }`}
          >
            {label(choice)}
          </button>
        ))}
        <label className="ms-auto inline-flex items-center gap-1.5">
          <span className="text-default text-[12px]">Or type</span>
          <span
            className={`inline-flex h-9 items-center rounded-lg border bg-white px-2.5 ${
              typedOwn ? 'border-primary' : 'border-border-color'
            }`}
          >
            {mode === 'amount' && <span className="text-default">{currency}</span>}
            <input
              inputMode="decimal"
              aria-label={`${title}, ${mode === 'pct' ? 'percent' : 'amount'}`}
              value={text}
              disabled={disabled}
              placeholder="0"
              onChange={(event) => {
                const next = event.target.value.replace(/[^\d.]/g, '');

                setText(next);
                onChange(mode, next === '' || Number.isNaN(Number(next)) ? null : Number(next));
              }}
              className="font-mono-nums w-[72px] bg-transparent px-1 text-end font-semibold text-gray-900 outline-none"
            />
            {mode === 'pct' && <span className="text-default">%</span>}
          </span>
        </label>
      </div>

      {children}
    </div>
  );
}
